import { useState, useEffect, useCallback } from 'react';

interface UseControlledValueProps<T> {
  value?: T;
  onValueChange?: (value: T) => void;
  defaultValue?: T;
}

export const useControlledValue = <T>({ value, onValueChange, defaultValue }: UseControlledValueProps<T>) => {
  const [internalValue, setInternalValue] = useState<T | undefined>(value ?? defaultValue);

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const handleValueChange = useCallback((newValue: T | ((prevValue: T | undefined) => T)) => {
    setInternalValue(prevValue => {
      const resolvedValue = typeof newValue === 'function' 
        ? (newValue as (prevValue: T | undefined) => T)(prevValue)
        : newValue;
      
      onValueChange?.(resolvedValue);
      return resolvedValue;
    });
  }, [onValueChange]);

  return {
    value: internalValue,
    onValueChange: handleValueChange,
  };
};
